/**
 * Geolocation utility for detecting user's country and setting appropriate language
 */

// Country to language mapping based on GPS location
const COUNTRY_LANGUAGE_MAP = {
  // India
  'IN': 'hi',
  // Pakistan
  'PK': 'ur',
  // Saudi Arabia
  'SA': 'ar',
  // Iraq
  'IQ': 'ar',
  // Egypt
  'EG': 'ar',
  // Syria
  'SY': 'ar',
  // Jordan
  'JO': 'ar',
  // Lebanon
  'LB': 'ar',
  // Kuwait
  'KW': 'ar',
  // UAE
  'AE': 'ar',
  // Qatar
  'QA': 'ar',
  // Bahrain
  'BH': 'ar',
  // Oman
  'OM': 'ar',
  // Yemen
  'YE': 'ar',
  // Iran
  'IR': 'persian',
  // Afghanistan
  'AF': 'pashto',
  // France
  'FR': 'fr',
  // South Korea
  'KR': 'ko',
  // Philippines
  'PH': 'filipino',
  // Morocco
  'MA': 'ar',
  // Algeria
  'DZ': 'ar',
  // Tunisia
  'TN': 'ar',
  // Libya
  'LY': 'ar',
  // Sudan
  'SD': 'ar',
  // Somalia
  'SO': 'ar',
  // Djibouti
  'DJ': 'ar',
  // Mauritania
  'MR': 'ar',
  // Comoros
  'KM': 'ar',
  // Default fallback
  'DEFAULT': 'en'
};

/**
 * Get country code from latitude and longitude using reverse geocoding
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<string>} Country code (ISO 3166-1 alpha-2)
 */
export const getCountryFromCoordinates = async (latitude, longitude) => {
  console.log(`🌍 Getting country for coordinates: ${latitude}, ${longitude}`);
  
  // Try multiple geocoding services
  const geocodingServices = [
    // Primary service - BigDataCloud
    async () => {
      console.log('🔍 Trying BigDataCloud geocoding service...');
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      if (!response.ok) throw new Error('BigDataCloud API failed');
      const data = await response.json();
      return data.countryCode;
    },
    
    // Backup service - IP Geolocation API (doesn't require coordinates but we can still use it)
    async () => {
      console.log('🔍 Trying alternative geocoding with ipgeolocation.io...');
      const response = await fetch(
        `https://api.ipgeolocation.io/timezone?apiKey=free&lat=${latitude}&long=${longitude}`
      );
      if (!response.ok) throw new Error('ipgeolocation.io API failed');
      const data = await response.json();
      console.log('📊 ipgeolocation.io response:', data);
      return data.country_code2;
    },
    
    // Third option - geocode.xyz (free service)
    async () => {
      console.log('🔍 Trying geocode.xyz service...');
      const response = await fetch(
        `https://geocode.xyz/${latitude},${longitude}?json=1`
      );
      if (!response.ok) throw new Error('geocode.xyz API failed');
      const data = await response.json();
      console.log('📊 geocode.xyz response:', data);
      return data.prov; // This service returns different format
    }
  ];
  
  // Try each service in order
  for (let i = 0; i < geocodingServices.length; i++) {
    try {
      const countryCode = await geocodingServices[i]();
      if (countryCode) {
        return countryCode.toUpperCase();
      }
    } catch (error) {
      if (i === geocodingServices.length - 1) {
        return 'DEFAULT';
      }
    }
  }
  
  return 'DEFAULT';
};

/**
 * Get user's current position using GPS
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds timeout
      maximumAge: 300000 // 5 minutes cache
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        let errorMessage = 'Unknown geolocation error';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'User denied the request for Geolocation';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'The request to get user location timed out';
            break;
        }
        reject(new Error(errorMessage));
      },
      options
    );
  });
};

/**
 * Get language code based on user's GPS location
 * @returns {Promise<string>} Language code
 */
export const getLanguageFromGPS = async () => {
  try {
    
    // Get user's current position
    const position = await getCurrentPosition();
    
    // Get country code from coordinates
    const countryCode = await getCountryFromCoordinates(
      position.latitude, 
      position.longitude
    );
    
    // Map country to language
    const languageCode = COUNTRY_LANGUAGE_MAP[countryCode] || COUNTRY_LANGUAGE_MAP.DEFAULT;
    
    return languageCode;
  } catch (error) {
    // Return default language if GPS detection fails
    return COUNTRY_LANGUAGE_MAP.DEFAULT;
  }
};

/**
 * Get language based on browser locale as fallback
 * @returns {string} Language code
 */
export const getLanguageFromBrowser = () => {
  try {
    const browserLang = navigator.language || navigator.userLanguage;
    const langCode = browserLang.split('-')[0].toLowerCase();
    
    // Map common browser language codes to our supported languages
    const browserLanguageMap = {
      'hi': 'hi',    // Hindi
      'ar': 'ar',    // Arabic
      'ur': 'ur',    // Urdu
      'fr': 'fr',    // French
      'ko': 'ko',    // Korean
      'tl': 'filipino', // Tagalog/Filipino
      'fil': 'filipino', // Filipino
      'fa': 'persian',   // Persian/Farsi
      'ps': 'pashto',    // Pashto
      'en': 'en'     // English
    };
    
    return browserLanguageMap[langCode] || 'en';
  } catch (error) {
    console.warn('Failed to detect language from browser:', error);
    return 'en';
  }
};

/**
 * Comprehensive language detection with multiple fallbacks
 * Priority: GPS → Browser Locale → Default (English)
 * @returns {Promise<string>} Language code
 */
export const detectUserLanguage = async () => {
  try {
    console.log('Starting comprehensive language detection...');
    
    // First try GPS-based detection
    const gpsLanguage = await getLanguageFromGPS();
    if (gpsLanguage && gpsLanguage !== 'en') {
      return gpsLanguage;
    }
    
    // If GPS fails or returns default, try browser locale
    const browserLanguage = getLanguageFromBrowser();
    if (browserLanguage && browserLanguage !== 'en') {
      return browserLanguage;
    }
    
    return 'en';
  } catch (error) {
    return 'en';
  }
};

/**
 * Check if geolocation is available and permissions are granted
 * @returns {Promise<boolean>}
 */
export const checkGeolocationPermission = async () => {
  if (!navigator.geolocation) {
    return false;
  }
  
  try {
    // Check if permission is already granted
    if (navigator.permissions) {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      return permission.state === 'granted';
    }
    
    // If permissions API is not available, we can't pre-check
    return true;
  } catch (error) {
    console.warn('Could not check geolocation permission:', error);
    return false;
  }
};

export default {
  getCountryFromCoordinates,
  getCurrentPosition,
  getLanguageFromGPS,
  getLanguageFromBrowser,
  detectUserLanguage,
  checkGeolocationPermission,
  COUNTRY_LANGUAGE_MAP
};
